import {
  EstadoActualRequisito,
  type EvaluacionTernaAnteriorDTO,
  type EvaluacionTernaDTO,
  type RequisitoConAsesoria,
  RequisitoEvidencias,
  requisitosService,
} from '@/services/requisitos.service';
import type { CumplimientoNTC, Evidencia } from '@/types/types';
import { useCallback, useEffect, useState } from 'react';

interface CombinacionInfo {
  combinacionId: number;
  nombre: string;
  ntcs: { ntcId: number; nombre: string; orden: number }[];
}

interface UseRequisitosEvaluacionResult {
  requisitos: RequisitoConAsesoria[];
  ntcs: CumplimientoNTC[];
  evidencias: Evidencia[];
  ternas: import('@/services/sysadmin.service').TernaDTO[];
  requisitosSinTerna: string[];
  evaluacionesTernas: EvaluacionTernaDTO[];
  evaluacionesTernasAnterior: EvaluacionTernaAnteriorDTO[];
  comentariosAnteriores: import('@/services/requisitos.service').ComentarioAnterior[];
  cambioFieldLogs: import('@/types/evaluacionAdmin').CambioFieldLog[];
  historialAnterior: import('@/utils/revisionHistorial').RevisionEntry[];
  combinacion: CombinacionInfo | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UserRequisitosEstadoActualResult {
  requisitos: EstadoActualRequisito[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Siempre usar sólo NITs válidos, nunca UUIDS para nit (verificado en el propio hook)
export function useRequisitosEvaluacion(
  nit: string,
  asesoriaId?: number,
  ntcId?: string,
  enabled: boolean = true,
  requisitoId?: string
): UseRequisitosEvaluacionResult {
  const [requisitos, setRequisitos] = useState<RequisitoEvidencias[]>([]);
  const [ntcs, setNtcs] = useState<CumplimientoNTC[]>([]);
  const [evidencias, setEvidencias] = useState<Evidencia[]>([]);
  const [ternas, setTernas] = useState<import('@/services/sysadmin.service').TernaDTO[]>([]);
  const [requisitosSinTerna, setRequisitosSinTerna] = useState<string[]>([]);
  const [evaluacionesTernas, setEvaluacionesTernas] = useState<EvaluacionTernaDTO[]>([]);
  const [evaluacionesTernasAnterior, setEvaluacionesTernasAnterior] = useState<EvaluacionTernaAnteriorDTO[]>([]);
  const [comentariosAnteriores, setComentariosAnteriores] = useState<import('@/services/requisitos.service').ComentarioAnterior[]>([]);
  const [cambioFieldLogs, setCambioFieldLogs] = useState<import('@/types/evaluacionAdmin').CambioFieldLog[]>([]);
  const [historialAnterior, setHistorialAnterior] = useState<import('@/utils/revisionHistorial').RevisionEntry[]>([]);
  const [combinacion, setCombinacion] = useState<CombinacionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Resetear isLoading de forma síncrona en el render al cambiar parámetros para evitar parpadeos visuales
  const [prevParams, setPrevParams] = useState({ nit, asesoriaId, ntcId, enabled, requisitoId });
  if (
    prevParams.nit !== nit ||
    prevParams.asesoriaId !== asesoriaId ||
    prevParams.ntcId !== ntcId ||
    prevParams.enabled !== enabled ||
    prevParams.requisitoId !== requisitoId
  ) {
    setPrevParams({ nit, asesoriaId, ntcId, enabled, requisitoId });
    setIsLoading(true);
  }

  const fetchRequisitos = useCallback(async () => {
    if (!enabled || !nit || !/^\d{7,12}$/.test(nit)) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const requisitosResponse = await requisitosService.getEvaluacion(nit, asesoriaId, ntcId, requisitoId);

      if (requisitosResponse.success) {
        setRequisitos(requisitosResponse.requisitos);
        setNtcs(requisitosResponse.ntcs);
        setEvidencias(requisitosResponse.evidencias || []);
        setTernas(requisitosResponse.ternas || []);
        setRequisitosSinTerna(requisitosResponse.requisitosSinTerna || []);
        setEvaluacionesTernas(requisitosResponse.evaluacionesTernas || []);
        setEvaluacionesTernasAnterior(requisitosResponse.evaluacionesTernasAnterior || []);
        setComentariosAnteriores(requisitosResponse.comentariosAnteriores || []);
        setCambioFieldLogs(requisitosResponse.cambioFieldLogs || []);
        setHistorialAnterior(requisitosResponse.historialAnterior || []);
        setCombinacion(requisitosResponse.combinacion ?? null);
      } else {
        setError(requisitosResponse.message || 'Error al cargar requisitos');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar requisitos');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, nit, asesoriaId, ntcId, requisitoId]);

  useEffect(() => {
    fetchRequisitos();
  }, [fetchRequisitos]);

  return {
    requisitos,
    ntcs,
    evidencias,
    ternas,
    requisitosSinTerna,
    evaluacionesTernas,
    evaluacionesTernasAnterior,
    comentariosAnteriores,
    cambioFieldLogs,
    historialAnterior,
    combinacion,
    isLoading,
    error,
    refetch: fetchRequisitos,
  };
}

export function useRequisitosEstadoActual(nit: string, ntcId?: string, enabled: boolean = true): UserRequisitosEstadoActualResult {
  const [requisitos, setRequisitos] = useState<EstadoActualRequisito[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRequisitos = useCallback(async () => {
    if (!enabled || !nit || !/^\d{7,12}$/.test(nit)) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const requisitosResponse = await requisitosService.getEstadoActual(nit, ntcId);

      if (requisitosResponse.success) {
        setRequisitos(requisitosResponse.estadoActual || []);
      } else {
        setError(requisitosResponse.message || 'Error al cargar estado actual de requisitos');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar estado actual de requisitos');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, nit, ntcId]);

  useEffect(() => {
    fetchRequisitos();
  }, [fetchRequisitos]);

  return {
    requisitos,
    isLoading,
    error,
    refetch: fetchRequisitos,
  };
}
